import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";

import { PaymentTermsLearnMore } from "@faire/retailer/components/PaymentTerms/PaymentTermsLearnMore";
import { getSettingOwFImproveTermsTouchpointsPDP } from "@faire/retailer/settings/getSettingOwFImproveTermsTouchpointsPDP";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

import {
  getAvailableUnits,
  shouldShowAvailableUnits,
} from "./productAvailableUnits";

interface IProps {
  nonReturnableReason: string | undefined;
  showPreorderMessaging: boolean;
  product: IProductPageResponse.IPdpProduct;
  selectedOption?: IProductOption;
}

export const LoggedInOnlyNote: React.FC<IProps> = ({
  nonReturnableReason,
  showPreorderMessaging,
  product,
  selectedOption,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isRetailerOnNetTerms } = useRetailerStore(["isRetailerOnNetTerms"]);
  const availableUnits = getAvailableUnits(product, selectedOption);
  const showAvailableUnits = shouldShowAvailableUnits(
    product,
    availableUnits,
    selectedOption
  );

  const availableUnitsMessage = showAvailableUnits
    ? strictLocalize(
        {
          defaultMessage:
            "{availableUnitsNumber, plural, one{Only # left!} other{Only # left!}}",
          description: {
            text: "current remaining stock of this product on Faire",
            img: "https://cdn.faire.com/fastly/b86003d7c49c55f2db5cdc0e3c53998bd218b186ee70d426e19311bf1e5069ec.png",
          },
        },
        { availableUnitsNumber: availableUnits }
      )
    : undefined;

  const finalSaleMessage = nonReturnableReason
    ? strictLocalize(
        {
          defaultMessage:
            "Final sale. {nonReturnableReasonFullyTranslatedSentence}",
          description: {
            text: "Reason of why this product is not returnable",
            img: "https://cdn.faire.com/fastly/a61b22c359fe1972f35652379bdb6b05c0a8b3d19fe7167199da3cdbe3ee5ca1.png",
          },
        },
        { nonReturnableReasonFullyTranslatedSentence: nonReturnableReason }
      )
    : undefined;

  if (nonReturnableReason || showAvailableUnits) {
    return (
      <>
        <Spacer height="2x" />
        <Typography>
          {[
            availableUnitsMessage,
            getSettingOwFImproveTermsTouchpointsPDP()
              ? nonReturnableReason
              : finalSaleMessage,
          ]
            .filter(isNotUndefined)
            .join(" ")}
        </Typography>
      </>
    );
  }

  if (
    !showPreorderMessaging &&
    isRetailerOnNetTerms &&
    getSettingOwFImproveTermsTouchpointsPDP()
  ) {
    return (
      <>
        <Spacer height="2x" />
        <PaymentTermsLearnMore displayLocation="PRODUCT_DETAILS" />
      </>
    );
  }

  return null;
};
